/**
 * ScribePanel
 *
 * Three-phase intake flow:
 *   1. Input  — textarea + optional voice + Parse button
 *   2. Review — proposal cards per extracted item (accept / edit / skip)
 *   3. Done   — summary with links to created items
 *
 * The panel is self-contained and works both as a full page and
 * as an embedded component.
 */

import { useState, useRef, useEffect, useCallback } from 'react';
import { Link } from 'react-router-dom';
import {
  Mic,
  MicOff,
  Sparkles,
  Loader2,
  Check,
  X,
  Pencil,
  ChevronDown,
  ChevronUp,
  Tag,
  ArrowUpCircle,
  AlertCircle,
  RotateCcw,
  ExternalLink,
  CheckSquare,
  Link2,
} from 'lucide-react';
import { useScribeStore } from '../../../stores/prioritization/scribeStore';
import { setDirectClassification } from '../../../lib/itemClassificationService';
import { useItemsStore } from '../../../stores/prioritization/itemsStore';
import { useUserStore } from '../../../stores/prioritization/userStore';
import type { ScribeReviewItem } from '../../../stores/prioritization/scribeStore';
import type { ItemLanguageType } from '../../../types/prioritization/lifecycle';
import { CATEGORY_BADGE, CATEGORY_BADGE_FALLBACK } from '../../../lib/itemCategoryColors';
import { DEFAULT_MODEL } from '../../../../supabase/functions/_shared/modelRequest.ts';

// ── Helpers ────────────────────────────────────────────────────────────────────

const CATEGORY_LABELS: Record<ItemLanguageType, string> = {
  problem: 'Problem',
  opportunity: 'Opportunity',
  risk: 'Risk',
  solution: 'Solution',
  commitment: 'Commitment',
  investment: 'Investment',
};

function CategoryChip({ category }: { category: string }) {
  const label = CATEGORY_LABELS[category as ItemLanguageType] ?? category;
  const { bg, text, border } = CATEGORY_BADGE[category as ItemLanguageType] ?? CATEGORY_BADGE_FALLBACK;
  return (
    <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-semibold border ${bg} ${text} ${border}`}>
      <Tag className="h-3 w-3" />
      {label}
    </span>
  );
}

const CONFIDENCE_COLORS = {
  high:   'bg-[var(--green-dim)] text-[var(--green)] dark:text-[var(--text-ter)]',
  medium: 'bg-[var(--mustard-dim)] text-[var(--mustard)]',
  low:    'bg-[var(--coral-dim)] text-[var(--coral)]',
};

const ALL_CATEGORIES: ItemLanguageType[] = [
  'problem', 'opportunity', 'risk', 'solution', 'commitment', 'investment',
];

function AltitudeBadge({ level }: { level: number }) {
  return (
    <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-semibold bg-[var(--hover-tint)] text-[var(--text-sec)] border border-[var(--rule)]">
      <ArrowUpCircle className="h-3 w-3" />
      Alt {level}
    </span>
  );
}

// ── Web Speech API ─────────────────────────────────────────────────────────────

declare global {
  interface Window {
    SpeechRecognition: typeof SpeechRecognition;
    webkitSpeechRecognition: typeof SpeechRecognition;
  }
}

function isSpeechSupported() {
  return typeof window !== 'undefined' &&
    ('SpeechRecognition' in window || 'webkitSpeechRecognition' in window);
}

// ── Proposal card ─────────────────────────────────────────────────────────────

function ProposalCard({
  proposal,
  index,
}: {
  proposal: ScribeReviewItem;
  index: number;
}) {
  const acceptProposal = useScribeStore((s) => s.acceptProposal);
  const skipProposal = useScribeStore((s) => s.skipProposal);
  const startEditProposal = useScribeStore((s) => s.startEditProposal);
  const saveEditProposal = useScribeStore((s) => s.saveEditProposal);
  const cancelEditProposal = useScribeStore((s) => s.cancelEditProposal);

  const [showRationale, setShowRationale] = useState(false);

  // Local edit state
  const [editTitle, setEditTitle] = useState('');
  const [editDescription, setEditDescription] = useState('');
  const [editCategory, setEditCategory] = useState<ItemLanguageType>('problem');
  const [editAltitude, setEditAltitude] = useState(3);

  const isEditing = proposal.status === 'editing';
  const isAccepted = proposal.status === 'accepted';
  const isSkipped = proposal.status === 'skipped';
  const isPending = proposal.status === 'pending';

  // Seed edit form when entering edit mode
  useEffect(() => {
    if (isEditing) {
      setEditTitle(proposal.editTitle ?? proposal.title);
      setEditDescription(proposal.editDescription ?? proposal.description);
      setEditCategory(proposal.editCategory ?? proposal.category);
      setEditAltitude(proposal.editAltitude ?? proposal.altitude);
    }
  }, [isEditing, proposal]);

  const displayTitle = proposal.editTitle ?? proposal.title;
  const displayCategory = proposal.editCategory ?? proposal.category;
  const displayAltitude = proposal.editAltitude ?? proposal.altitude;
  const displayDescription = proposal.editDescription ?? proposal.description;

  const borderColor = isAccepted
    ? 'border-[var(--green)] bg-[var(--green-dim)]'
    : isSkipped
    ? 'border-[var(--rule)] bg-[var(--surface)] opacity-50'
    : isEditing
    ? 'border-[var(--slate)] bg-[var(--slate-dim)]/30'
    : 'border-[var(--rule)] bg-[var(--bg-raised)] hover:border-[var(--rule)]';

  return (
    <div className={` border transition-all ${borderColor} overflow-hidden`}>
      {/* Card header */}
      <div className="px-4 py-3">
        <div className="flex items-start gap-3">
          {/* Index */}
          <span className="flex-shrink-0 w-5 h-5 rounded-full bg-[var(--hover-tint)] text-[var(--text-ter)] text-xs font-bold flex items-center justify-center mt-0.5">
            {index + 1}
          </span>

          <div className="flex-1 min-w-0">
            {/* Title + badges */}
            <div className="flex items-start gap-2 flex-wrap">
              <span className={`text-sm font-semibold ${isSkipped ? 'line-through text-[var(--text-ter)]' : 'text-[var(--text-pri)]'}`}>
                {displayTitle}
              </span>
            </div>
            <div className="flex items-center gap-2 mt-1.5 flex-wrap">
              <CategoryChip category={displayCategory} />
              <AltitudeBadge level={displayAltitude} />
              {proposal.confidence && (
                <span className={`px-1.5 py-0.5 rounded text-xs font-medium ${CONFIDENCE_COLORS[proposal.confidence]}`}>
                  {proposal.confidence} confidence
                </span>
              )}
            </div>

            {/* Description (collapsed by default unless editing) */}
            {!isEditing && displayDescription && (
              <p className="text-xs text-[var(--text-ter)] mt-1.5 leading-relaxed line-clamp-2">
                {displayDescription}
              </p>
            )}
          </div>

          {/* Status badge */}
          {isAccepted && (
            <span className="flex-shrink-0 inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-semibold bg-[var(--green-dim)] text-[var(--green)] dark:text-[var(--text-ter)]">
              <Check className="h-3 w-3" />
              Accepted
            </span>
          )}
          {isSkipped && (
            <span className="flex-shrink-0 inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-semibold bg-[var(--hover-tint)] text-[var(--text-ter)]">
              <X className="h-3 w-3" />
              Skipped
            </span>
          )}
        </div>
      </div>

      {/* Edit form */}
      {isEditing && (
        <div className="px-4 pb-3 border-t border-[var(--slate)] pt-3 space-y-3">
          <div>
            <label className="block text-xs font-medium text-[var(--text-sec)] mb-1">Title</label>
            <input
              type="text"
              value={editTitle}
              onChange={(e) => setEditTitle(e.target.value)}
              className="w-full text-sm border border-[var(--rule)] px-3 py-1.5 focus:outline-none focus:ring-2 focus:ring-blue-500"
            />
          </div>
          <div>
            <label className="block text-xs font-medium text-[var(--text-sec)] mb-1">Description</label>
            <textarea
              value={editDescription}
              onChange={(e) => setEditDescription(e.target.value)}
              rows={2}
              className="w-full text-sm border border-[var(--rule)] px-3 py-1.5 focus:outline-none focus:ring-2 focus:ring-blue-500 resize-none"
            />
          </div>
          <div className="flex gap-3">
            <div className="flex-1">
              <label className="block text-xs font-medium text-[var(--text-sec)] mb-1">Category</label>
              <select
                value={editCategory}
                onChange={(e) => setEditCategory(e.target.value as ItemLanguageType)}
                className="w-full text-sm border border-[var(--rule)] px-3 py-1.5 bg-[var(--bg-raised)] focus:outline-none focus:ring-2 focus:ring-blue-500"
              >
                {ALL_CATEGORIES.map((c) => (
                  <option key={c} value={c}>{CATEGORY_LABELS[c]}</option>
                ))}
              </select>
            </div>
            <div>
              <label className="block text-xs font-medium text-[var(--text-sec)] mb-1">Altitude</label>
              <select
                value={editAltitude}
                onChange={(e) => setEditAltitude(Number(e.target.value))}
                className="w-full text-sm border border-[var(--rule)] px-3 py-1.5 bg-[var(--bg-raised)] focus:outline-none focus:ring-2 focus:ring-blue-500"
              >
                {[1, 2, 3, 4, 5].map((n) => (
                  <option key={n} value={n}>{n}</option>
                ))}
              </select>
            </div>
          </div>
          <div className="flex gap-2 pt-1">
            <button
              type="button"
              onClick={() => saveEditProposal(proposal._localId, {
                title: editTitle,
                description: editDescription,
                category: editCategory,
                altitude: editAltitude,
              })}
              className="inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-semibold bg-[var(--slate)] text-white hover:bg-[var(--slate)] transition-colors"
            >
              <Check className="h-3.5 w-3.5" />
              Save &amp; accept
            </button>
            <button
              type="button"
              onClick={() => cancelEditProposal(proposal._localId)}
              className="px-3 py-1.5 text-xs font-medium border border-[var(--rule)] text-[var(--text-sec)] hover:bg-[var(--surface)] transition-colors"
            >
              Cancel
            </button>
          </div>
        </div>
      )}

      {/* Rationale + hints */}
      {!isEditing && (proposal.rationale || proposal.relationshipHints.length > 0) && (
        <div className="px-4 pb-3">
          <button
            type="button"
            onClick={() => setShowRationale((v) => !v)}
            className="flex items-center gap-1 text-xs text-[var(--text-ter)] hover:text-[var(--text-sec)] transition-colors"
          >
            {showRationale ? <ChevronUp className="h-3 w-3" /> : <ChevronDown className="h-3 w-3" />}
            {showRationale ? 'Hide details' : 'Show rationale'}
          </button>

          {showRationale && (
            <div className="mt-2 border border-[var(--rule)] bg-[var(--surface)] px-3 py-2 space-y-2">
              {proposal.rationale && (
                <p className="text-xs text-[var(--text-sec)] leading-relaxed">{proposal.rationale}</p>
              )}
              {proposal.relationshipHints.length > 0 && (
                <div className="flex items-start gap-1.5 flex-wrap">
                  <Link2 className="h-3 w-3 text-[var(--text-ter)] mt-0.5 flex-shrink-0" />
                  <span className="text-xs text-[var(--text-ter)]">Possible relationships:</span>
                  {proposal.relationshipHints.map((hint) => (
                    <span
                      key={hint}
                      className="px-1.5 py-0.5 rounded bg-[var(--hover-tint)] text-xs text-[var(--text-sec)]"
                    >
                      {hint}
                    </span>
                  ))}
                </div>
              )}
            </div>
          )}
        </div>
      )}

      {/* Action buttons */}
      {(isPending || isAccepted || isSkipped) && !isEditing && (
        <div className="flex items-center gap-2 px-4 pb-3">
          {isPending && (
            <>
              <button
                type="button"
                onClick={() => acceptProposal(proposal._localId)}
                className="inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-semibold bg-[var(--green)] text-white hover:bg-[var(--green)] transition-colors"
              >
                <Check className="h-3.5 w-3.5" />
                Accept
              </button>
              <button
                type="button"
                onClick={() => startEditProposal(proposal._localId)}
                className="inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium border border-[var(--rule)] text-[var(--text-sec)] hover:bg-[var(--surface)] transition-colors"
              >
                <Pencil className="h-3.5 w-3.5" />
                Edit
              </button>
              <button
                type="button"
                onClick={() => skipProposal(proposal._localId)}
                className="inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium text-[var(--text-ter)] hover:text-[var(--text-sec)] transition-colors"
              >
                <X className="h-3.5 w-3.5" />
                Skip
              </button>
            </>
          )}
          {(isAccepted || isSkipped) && (
            <button
              type="button"
              onClick={() =>
                isAccepted
                  ? skipProposal(proposal._localId)
                  : acceptProposal(proposal._localId)
              }
              className="text-xs text-[var(--text-ter)] hover:text-[var(--text-sec)] transition-colors"
            >
              {isAccepted ? 'Undo accept' : 'Undo skip'}
            </button>
          )}
        </div>
      )}
    </div>
  );
}

// ── Main panel ─────────────────────────────────────────────────────────────────

interface ScribePanelProps {
  workspaceId: string;
}

export function ScribePanel({ workspaceId }: ScribePanelProps) {
  const phase = useScribeStore((s) => s.phase);
  const inputText = useScribeStore((s) => s.inputText);
  const parseError = useScribeStore((s) => s.parseError);
  const createError = useScribeStore((s) => s.createError);
  const proposals = useScribeStore((s) => s.proposals);
  const createdItemIds = useScribeStore((s) => s.createdItemIds);
  const config = useScribeStore((s) => s.config);

  const fetchConfig = useScribeStore((s) => s.fetchConfig);
  const setInputText = useScribeStore((s) => s.setInputText);
  const parseInput = useScribeStore((s) => s.parseInput);
  const acceptAll = useScribeStore((s) => s.acceptAll);
  const createAccepted = useScribeStore((s) => s.createAccepted);
  const reset = useScribeStore((s) => s.reset);
  const backToInput = useScribeStore((s) => s.backToInput);

  const createItem = useItemsStore((s) => s.createItem);
  const userId = useUserStore((s) => s.userId);
  const mayConfigure = useUserStore((s) => s.can)('admin.settings');
  // Scribe is on when its settings row says so (migration 942 wrote one, on, for every workspace).
  const scribeOn = config?.enabled === true;
  const configLoading = useScribeStore((s) => s.isLoading);

  // Voice recording state
  const [isListening, setIsListening] = useState(false);
  const [voiceSupported] = useState(isSpeechSupported);
  const recognitionRef = useRef<SpeechRecognition | null>(null);
  const interimRef = useRef('');

  useEffect(() => {
    if (workspaceId) void fetchConfig(workspaceId);
  }, [workspaceId, fetchConfig]);

  // Voice input
  const startListening = useCallback(() => {
    if (!voiceSupported) return;
    const SpeechRec = window.SpeechRecognition ?? window.webkitSpeechRecognition;
    const recognition = new SpeechRec();
    recognition.continuous = true;
    recognition.interimResults = true;
    recognition.lang = 'en-US';

    recognition.onresult = (event) => {
      let interim = '';
      let final = '';
      for (let i = event.resultIndex; i < event.results.length; i++) {
        const transcript = event.results[i][0].transcript;
        if (event.results[i].isFinal) {
          final += transcript + ' ';
        } else {
          interim = transcript;
        }
      }
      if (final) {
        setInputText(inputText + final);
        interimRef.current = '';
      } else {
        interimRef.current = interim;
      }
    };

    recognition.onerror = () => setIsListening(false);
    recognition.onend = () => setIsListening(false);

    recognitionRef.current = recognition;
    recognition.start();
    setIsListening(true);
  }, [voiceSupported, inputText, setInputText]);

  const stopListening = useCallback(() => {
    recognitionRef.current?.stop();
    setIsListening(false);
  }, []);

  // Item creation callback
  const handleCreateItem = useCallback(
    async (item: {
      id: string;
      title: string;
      description: string;
      category: ItemLanguageType;
      altitude: number;
      workspaceId: string;
    }) => {
      const newId = crypto.randomUUID();
      createItem({
        id: newId,
        title: item.title,
        description: item.description,
        workspaceId: item.workspaceId,
        creationProvenance: 'ai_draft',
        creationSource: 'ai',
        creationReviewStatus: 'none',
      } as Parameters<typeof createItem>[0]);

      // Give the store a tick to process, then set classification
      await new Promise((r) => setTimeout(r, 200));
      await setDirectClassification({
        itemId: newId,
        category: item.category,
        altitudeLevel: item.altitude,
        userId,
      });

      return newId;
    },
    [createItem, userId],
  );

  const acceptedCount = proposals.filter((p) => p.status === 'accepted').length;
  const pendingCount = proposals.filter((p) => p.status === 'pending').length;
  const totalCount = proposals.length;

  // ── Phase: input ────────────────────────────────────────────────────────────

  if (phase === 'idle' || phase === 'parsing') {
    return (
      <div className="space-y-4">
        <div className="relative">
          <textarea
            value={inputText}
            onChange={(e) => setInputText(e.target.value)}
            disabled={phase === 'parsing'}
              placeholder={`Describe your items, ideas, problems, or opportunities in plain language...\n\nExamples:\n- "Create a new item to open an office in Paris - it's an opportunity related to global expansion"\n- "We have three issues: the login is broken, the dashboard is slow, and we're missing a mobile app"`}
            className="w-full min-h-[180px] text-sm border border-[var(--rule)] px-4 py-3 focus:outline-none focus:ring-2 focus:ring-blue-500 resize-y disabled:opacity-60 bg-[var(--bg-raised)] leading-relaxed"
          />
          {voiceSupported && (
            <button
              type="button"
              onClick={isListening ? stopListening : startListening}
              disabled={phase === 'parsing'}
              className={`absolute bottom-3 right-3 p-2  transition-colors ${
                isListening
                  ? 'bg-[var(--coral-dim)] text-[var(--coral)] hover:bg-[var(--coral-dim)] animate-pulse'
                  : 'bg-[var(--hover-tint)] text-[var(--text-ter)] hover:bg-[var(--hover-tint)]'
              } disabled:opacity-50`}
              title={isListening ? 'Stop recording' : 'Start voice input'}
            >
              {isListening ? <MicOff className="h-4 w-4" /> : <Mic className="h-4 w-4" />}
            </button>
          )}
        </div>

        {isListening && (
          <div className="flex items-center gap-2 text-xs text-[var(--coral)]">
            <span className="inline-block w-2 h-2 rounded-full bg-[var(--coral)] animate-ping" />
            Listening… speak now
          </div>
        )}

        {parseError && (
          <div className="flex items-start gap-2 p-3 bg-[var(--coral-dim)] border border-[var(--coral)] text-xs text-[var(--coral)]">
            <AlertCircle className="h-4 w-4 flex-shrink-0 mt-0.5" />
            {parseError}
          </div>
        )}

        {!scribeOn && !configLoading && (
          <div className="flex items-start gap-2 p-3 bg-[var(--mustard-dim)] border border-[var(--mustard)] text-xs text-[var(--mustard)]">
            <AlertCircle className="h-4 w-4 flex-shrink-0 mt-0.5" />
            {mayConfigure ? (
              <span>
                Scribe is off for this workspace.{' '}
                <Link to="/prioritization/settings/scribe-agent" className="underline font-medium">Turn it on</Link>
              </span>
            ) : (
              <span>Scribe is off for this workspace. A workspace administrator can turn it on.</span>
            )}
          </div>
        )}

        <div className="flex items-center gap-3">
          {scribeOn && <button
            type="button"
            onClick={() => void parseInput(inputText)}
            disabled={phase === 'parsing' || !inputText.trim()}
            className="inline-flex items-center gap-2 px-4 py-2 text-sm font-semibold bg-[var(--slate)] text-white hover:bg-[var(--slate)] disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
          >
            {phase === 'parsing' ? (
              <Loader2 className="h-4 w-4 animate-spin" />
            ) : (
              <Sparkles className="h-4 w-4" />
            )}
            {phase === 'parsing' ? 'Parsing…' : 'Parse with Scribe'}
          </button>}

          {inputText && (
            <button
              type="button"
              onClick={reset}
              className="text-xs text-[var(--text-ter)] hover:text-[var(--text-sec)] transition-colors"
            >
              Clear
            </button>
          )}

          <span className="ml-auto text-xs text-[var(--text-ter)]">
            Model: {config?.model ?? DEFAULT_MODEL}
          </span>
        </div>
      </div>
    );
  }

  // ── Phase: reviewing ────────────────────────────────────────────────────────

  if (phase === 'reviewing') {
    return (
      <div className="space-y-4">
        {/* Review header */}
        <div className="flex items-center justify-between">
          <div>
            <h3 className="text-sm font-semibold text-[var(--text-pri)]">
              {totalCount} item{totalCount !== 1 ? 's' : ''} found
            </h3>
            <p className="text-xs text-[var(--text-ter)] mt-0.5">
              Review each proposal, then create the ones you want to keep.
            </p>
          </div>
          <div className="flex items-center gap-3">
            {pendingCount > 0 && (
              <button
                type="button"
                onClick={acceptAll}
                className="inline-flex items-center gap-1.5 text-xs font-medium text-[var(--text-ter)] hover:text-[var(--text-ter)] transition-colors"
              >
                <CheckSquare className="h-3.5 w-3.5" />
                Accept all
              </button>
            )}
            <button
              type="button"
              onClick={backToInput}
              className="inline-flex items-center gap-1.5 text-xs text-[var(--text-ter)] hover:text-[var(--text-sec)] transition-colors"
            >
              <RotateCcw className="h-3.5 w-3.5" />
              Try again
            </button>
          </div>
        </div>

        {/* Proposal cards */}
        <div className="space-y-3">
          {proposals.map((proposal, i) => (
            <ProposalCard key={proposal._localId} proposal={proposal} index={i} />
          ))}
        </div>

        {createError && (
          <div className="flex items-start gap-2 p-3 bg-[var(--coral-dim)] border border-[var(--coral)] text-xs text-[var(--coral)]">
            <AlertCircle className="h-4 w-4 flex-shrink-0 mt-0.5" />
            {createError}
          </div>
        )}

        {/* Create button */}
        <div className="flex items-center gap-3 pt-1 border-t border-[var(--rule)]">
          <button
            type="button"
            onClick={() =>
              void createAccepted({
                workspaceId,
                userId,
                onCreate: handleCreateItem,
              })
            }
            disabled={acceptedCount === 0}
            className="inline-flex items-center gap-2 px-4 py-2 text-sm font-semibold bg-[var(--green)] text-white hover:bg-[var(--green)] disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
          >
            <Check className="h-4 w-4" />
            Create {acceptedCount} item{acceptedCount !== 1 ? 's' : ''}
          </button>
          <span className="text-xs text-[var(--text-ter)]">
            {acceptedCount} accepted · {proposals.filter((p) => p.status === 'skipped').length} skipped · {pendingCount} pending
          </span>
        </div>
      </div>
    );
  }

  // ── Phase: creating ─────────────────────────────────────────────────────────

  if (phase === 'creating') {
    return (
      <div className="flex flex-col items-center justify-center py-12 gap-3">
        <Loader2 className="h-8 w-8 animate-spin text-[var(--text-ter)]" />
        <p className="text-sm text-[var(--text-sec)] font-medium">Creating items…</p>
      </div>
    );
  }

  // ── Phase: done ─────────────────────────────────────────────────────────────

  return (
    <div className="space-y-4">
      <div className="border border-[var(--green)] bg-[var(--green-dim)] px-5 py-4">
        <div className="flex items-center gap-3">
          <div className="flex-shrink-0 w-8 h-8 rounded-full bg-[var(--green-dim)] flex items-center justify-center">
            <Check className="h-4 w-4 text-[var(--green)]" />
          </div>
          <div>
            <p className="text-sm font-semibold text-[var(--green)] dark:text-[var(--text-ter)]">
              {createdItemIds.length} item{createdItemIds.length !== 1 ? 's' : ''} created
            </p>
            <p className="text-xs text-[var(--green)] dark:text-[var(--text-ter)] mt-0.5">
              Items are now in your workspace at intake altitude. Review them below.
            </p>
          </div>
        </div>
      </div>

      <div className="space-y-2">
        {createdItemIds.map((id) => (
          <Link
            key={id}
            to={`/prioritization/items/${id}`}
            className="flex items-center gap-2 px-3 py-2 border border-[var(--rule)] hover:border-[var(--slate)] hover:bg-[var(--slate-dim)] transition-colors text-sm text-[var(--text-sec)] no-underline"
          >
            <ExternalLink className="h-3.5 w-3.5 text-[var(--text-ter)] flex-shrink-0" />
            View item
            <span className="text-xs text-[var(--text-ter)] font-mono ml-auto">{id.slice(0, 8)}…</span>
          </Link>
        ))}
      </div>

      <button
        type="button"
        onClick={reset}
        className="inline-flex items-center gap-2 px-4 py-2 text-sm font-medium border border-[var(--rule)] text-[var(--text-sec)] hover:bg-[var(--surface)] transition-colors"
      >
        <Sparkles className="h-4 w-4" />
        Parse more items
      </button>
    </div>
  );
}
